import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError, formatEuro } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ArrowLeft, FileText, Loader2, Plus, Trash2, Check, X } from "lucide-react";

const newUid = () => `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function DevisImport() {
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [titre, setTitre] = useState("");
  const [objet, setObjet] = useState("");
  const [conditions, setConditions] = useState("");
  const [lignes, setLignes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les PDFs sont supportés");
      return;
    }
    setParsing(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/devis/import", data);
      setParsed(res);
      setTitre(res.titre || "Devis importé");
      setObjet("");
      setConditions(res.conditions || "");
      setLignes((res.lignes || []).map((l) => ({ ...l, _uid: newUid() })));
      if (res.matched_client_id) {
        setClientId(res.matched_client_id);
        toast.success(`PDF analysé · client reconnu (${res.lignes.length} lignes)`);
      } else {
        toast.success(`PDF analysé · ${res.lignes.length} lignes extraites — sélectionne un client`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'import");
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  const setLine = (i, k, v) =>
    setLignes((s) => {
      const next = [...s];
      next[i] = { ...next[i], [k]: v };
      return next;
    });
  const removeLine = (i) => setLignes((s) => s.filter((_, idx) => idx !== i));
  const addLine = () =>
    setLignes((s) => [...s, { designation: "", description: "", quantite: 1, unite: "u", prix_unitaire: 0, tva: 0, _uid: newUid() }]);

  const totalHT = lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire || 0), 0);

  const submit = async () => {
    if (!clientId) {
      toast.error("Sélectionne un client");
      return;
    }
    if (!titre.trim() || lignes.length === 0) {
      toast.error("Titre + au moins une ligne requis");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/devis", {
        client_id: clientId,
        titre: titre.trim(),
        objet: objet.trim() || null,
        lignes: lignes.map(({ _uid, ...l }) => ({
          ...l,
          quantite: Number(l.quantite),
          prix_unitaire: Number(l.prix_unitaire),
          tva: Number(l.tva),
        })),
        conditions: conditions.trim() || null,
        validite_jours: 30,
      });
      toast.success(`Devis ${data.numero} créé`);
      navigate(`/admin/devis/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="devis-import-page">
      <Link to="/admin/devis" className="text-eyebrow inline-flex items-center gap-2 link-underline-static mb-6">
        <ArrowLeft size={14} /> Retour aux devis
      </Link>

      <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-eyebrow mb-3">Import devis</p>
          <h1 className="h-display text-4xl md:text-5xl">Depuis un PDF.</h1>
          <p className="text-muted-foreground text-[13.5px] mt-3 max-w-xl">
            EBP, Sage, Henrri, ProDevis… L'IA extrait les lignes, tu valides, le devis est créé en
            un clic — avec ton numéro de série.
          </p>
        </div>
        {!parsed && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing}
              className="inline-flex items-center gap-3 bg-foreground text-background px-6 py-4 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-primary disabled:opacity-60 transition-colors"
              data-testid="import-pick-pdf"
            >
              {parsing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {parsing ? "Analyse en cours…" : "Choisir un PDF"}
            </button>
            <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={onFile} />
          </>
        )}
      </div>

      {!parsed && !parsing && (
        <div className="border border-dashed border-border rounded-sm p-12 text-center bg-muted/30">
          <FileText size={40} className="mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-[13px] text-muted-foreground max-w-md mx-auto">
            Glisse-dépose ton PDF ici ou clique sur <strong>Choisir un PDF</strong>. L'extraction
            prend 5 à 10 secondes selon la longueur du devis.
          </p>
        </div>
      )}

      {parsed && (
        <div className="space-y-8 max-w-5xl">
          {/* Recap header */}
          <div className="border border-border bg-muted/20 p-4 grid md:grid-cols-3 gap-4 text-[12px]">
            <div>
              <p className="text-eyebrow mb-1">Devis source</p>
              <p className="font-medium">{parsed.numero_source || "—"}</p>
            </div>
            <div>
              <p className="text-eyebrow mb-1">Client détecté</p>
              <p className="font-medium">{parsed.client?.nom || "—"}</p>
              {parsed.client?.ville && (
                <p className="text-muted-foreground text-[11px] mt-0.5">
                  {parsed.client.cp} {parsed.client.ville}
                </p>
              )}
            </div>
            <div>
              <p className="text-eyebrow mb-1">Total source</p>
              <p className="font-medium">{formatEuro(parsed.total_ht)} HT</p>
              {parsed.tva_franchise && (
                <p className="text-[10px] text-primary mt-0.5 italic">Franchise TVA détectée</p>
              )}
            </div>
          </div>

          {/* Form: client + titre */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-eyebrow block mb-2">
                Client * {parsed.matched_client_id && <Check size={11} className="inline text-primary" />}
              </label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full bg-transparent border-b border-border py-2 text-[14px] outline-none"
                data-testid="import-client-select"
              >
                <option value="">— Sélectionne un client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </select>
              <Link to="/admin/clients" className="text-[11px] text-primary link-underline-static mt-1.5 inline-block">
                + Créer un nouveau client
              </Link>
            </div>
            <div>
              <label className="text-eyebrow block mb-2">Titre du devis *</label>
              <input
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                className="w-full bg-transparent border-b border-border py-2 text-[14px] outline-none"
                data-testid="import-titre"
              />
            </div>
          </div>

          <div>
            <label className="text-eyebrow block mb-2">Objet (optionnel)</label>
            <input
              value={objet}
              onChange={(e) => setObjet(e.target.value)}
              placeholder="Sous-titre court affiché sur le PDF"
              className="w-full bg-transparent border-b border-border py-2 text-[14px] outline-none"
            />
          </div>

          {/* Lignes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-eyebrow">{lignes.length} lignes</label>
              <button
                type="button"
                onClick={addLine}
                className="text-eyebrow text-primary link-underline-static"
              >
                <Plus size={12} className="inline mr-1" /> Ajouter une ligne
              </button>
            </div>
            <div className="space-y-3">
              {lignes.map((l, i) => (
                <div
                  key={l._uid}
                  className="border border-border p-4 grid md:grid-cols-12 gap-3"
                  data-testid={`import-line-${i}`}
                >
                  <div className="md:col-span-5">
                    <input
                      value={l.designation}
                      onChange={(e) => setLine(i, "designation", e.target.value)}
                      placeholder="Désignation"
                      className="w-full bg-transparent border-b border-border py-1.5 text-[13.5px] outline-none font-medium"
                    />
                    <textarea
                      value={l.description}
                      onChange={(e) => setLine(i, "description", e.target.value)}
                      placeholder="Description longue (optionnelle)"
                      className="w-full bg-transparent border-b border-border py-1.5 text-[12.5px] outline-none mt-2 text-muted-foreground min-h-[32px] resize-y"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Qté</label>
                    <input
                      type="number"
                      step="0.01"
                      value={l.quantite}
                      onChange={(e) => setLine(i, "quantite", e.target.value)}
                      className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Unité</label>
                    <input
                      value={l.unite}
                      onChange={(e) => setLine(i, "unite", e.target.value)}
                      className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">PU HT (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={l.prix_unitaire}
                      onChange={(e) => setLine(i, "prix_unitaire", e.target.value)}
                      className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Montant HT</label>
                    <div
                      className="w-full border-b border-border py-1.5 text-[13px] text-right tabular-nums text-primary font-medium"
                      data-testid={`import-line-montant-ht-${i}`}
                    >
                      {formatEuro(Number(l.quantite || 0) * Number(l.prix_unitaire || 0))}
                    </div>
                  </div>
                  <div className="md:col-span-1 flex items-end justify-end">
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conditions */}
          <div>
            <label className="text-eyebrow block mb-2">Conditions</label>
            <textarea
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              rows={3}
              className="w-full bg-transparent border border-border p-3 text-[13px] outline-none"
              placeholder="Acompte 30 % à la signature, solde à la livraison…"
            />
          </div>

          {/* Total */}
          <div className="border-t border-border pt-6 flex justify-end">
            <div className="text-right">
              <p className="text-serial">Total recalculé</p>
              <p className="font-serif text-3xl text-primary">{formatEuro(totalHT)}</p>
              {parsed.tva_franchise && (
                <p className="text-[10px] text-muted-foreground mt-2 italic">
                  TVA non applicable, art. 293 B du CGI
                </p>
              )}
              {Math.abs(totalHT - parsed.total_ht) > 0.5 && (
                <p className="text-[11px] text-destructive mt-2">
                  ⚠ Écart de {formatEuro(Math.abs(totalHT - parsed.total_ht))} avec le PDF source
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4">
            <button
              type="button"
              onClick={() => {
                setParsed(null);
                setLignes([]);
                setClientId("");
                setTitre("");
              }}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-medium text-muted-foreground hover:text-foreground"
            >
              <X size={12} /> Recommencer
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="bg-foreground text-background px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-primary transition-colors disabled:opacity-50"
              data-testid="import-create-devis"
            >
              {saving ? "Création…" : "Créer le devis"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
